import {
  faChevronDown,
  faDollarSign,
  faHashtag,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Calendar,
  CheckboxDropdown,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SearchableCheckboxDropdown,
} from '@repo/videa-components/src';
import { cn } from '@repo/videa-components/src/lib/utils';
import { startOfDay, subDays } from 'date-fns';
import { useAtom } from 'jotai';
import { useMemo, useRef, useState } from 'react';

import type { TProviderRole } from '@/react-query/insights/types';
import { useGetPracticeGroups } from '@/react-query/insights/usePracticeGroups';
import type { TPracticeGroup } from '@/react-query/insights/usePracticeGroups';
import { usePreferences } from '@/react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import {
  comparisonPeriodModeAtom,
  customPriorEndDateAtom,
  customPriorStartDateAtom,
  endDateAtom,
  isCountsAtom,
  materialityFilterOpportunityCountsAtom,
  materialityFilterOpportunityDollarsAtom,
  materialityFilterProductionCountsAtom,
  materialityFilterProductionDollarsAtom,
  selectedPracticeGroupsAtom,
  selectedPracticesAtom,
  selectedProviderRoleAtom,
  startDateAtom,
  taskTypeAtom,
  txTypeAtom,
} from '../../atoms';
import { useGetFilteredClinicalData } from '../../helpers/data/getClinicalData';
import type { TComparisonPresetKey } from '../../helpers/datePresets';
import {
  clampPriorPeriodToBeforeCurrent,
  getComparisonPresetRanges,
  getMatchingDatePreset,
  useDatePresetLabels,
  useDateRangePresetOptions,
  useFormatDateRangeDisplay,
  usePriorPeriod,
} from '../../helpers/datePresets';
import {
  useGetAvailableTasks,
  useGetAvailableTreatments,
} from '../../helpers/getAvailableTreatments';
import {
  taskCodesToTypes,
  taskTypesToCodes,
} from '../../helpers/taskTypeHelpers';

import { MaterialityFilter } from './MaterialityFilter';
import { SavedViews } from './SavedViews';

export const Filters = ({
  selectedDrilldownPractices,
  selectedDrilldownProviders,
  enableProviderRoleFilter,
  enableTasksFilter,
  enableMaterialityFilter,
  enablePracticeFilter,
}: {
  selectedDrilldownPractices?: string[];
  selectedDrilldownProviders?: string[];
  enableProviderRoleFilter?: boolean;
  enableTasksFilter?: boolean;
  enableMaterialityFilter?: boolean;
  enablePracticeFilter?: boolean;
}) => {
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();
  const [txType, setTxType] = useAtom(txTypeAtom);
  const [taskType, setTaskType] = useAtom(taskTypeAtom);
  const [selectedProviderRole, setSelectedProviderRole] = useAtom(
    selectedProviderRoleAtom,
  );

  const [startDate, setStartDate] = useAtom(startDateAtom);
  const [endDate, setEndDate] = useAtom(endDateAtom);
  const [comparisonPeriodMode, setComparisonPeriodMode] = useAtom(
    comparisonPeriodModeAtom,
  );
  const [priorStartDate, setPriorStartDate] = useAtom(customPriorStartDateAtom);
  const [priorEndDate, setPriorEndDate] = useAtom(customPriorEndDateAtom);

  const { priorStart, priorEnd } = usePriorPeriod();

  const [
    materialityFilterProductionDollars,
    setMaterialityFilterProductionDollars,
  ] = useAtom(materialityFilterProductionDollarsAtom);
  const [
    materialityFilterProductionCounts,
    setMaterialityFilterProductionCounts,
  ] = useAtom(materialityFilterProductionCountsAtom);
  const [
    materialityFilterOpportunityDollars,
    setMaterialityFilterOpportunityDollars,
  ] = useAtom(materialityFilterOpportunityDollarsAtom);
  const [
    materialityFilterOpportunityCounts,
    setMaterialityFilterOpportunityCounts,
  ] = useAtom(materialityFilterOpportunityCountsAtom);

  const [selectedPractices, setSelectedPractices] = useAtom(
    selectedPracticesAtom,
  );
  const [selectedRegions, setSelectedRegions] = useAtom(
    selectedPracticeGroupsAtom,
  );
  const [isCounts, setIsCounts] = useAtom(isCountsAtom);

  const availableTreatments = useGetAvailableTreatments(
    selectedDrilldownPractices,
    selectedDrilldownProviders,
  );

  const availableTasks = useGetAvailableTasks(
    selectedDrilldownPractices,
    selectedDrilldownProviders,
  );

  const { data: practiceGroups } = useGetPracticeGroups();

  const { data: insights } = useGetFilteredClinicalData({
    isPracticesEnabled: false,
  });

  const { data: prefs } = usePreferences();

  const practices =
    insights?.practices?.map((practice) => ({
      id: practice.practiceVideaId,
      name: practice.practiceName,
    })) ?? [];

  const pilotRegionPractices = practiceGroups?.data
    ?.find((group) => group.id === prefs?.enableInsightsPilotRegion)
    ?.practices.map((practice) => practice.practiceId);

  const activePracticeGroups = practiceGroups?.data.filter(
    (group) =>
      group.active &&
      (!prefs?.enableInsightsPilotRegion ||
        group?.practices?.every((practice) =>
          pilotRegionPractices?.includes(practice.practiceId),
        )),
  );

  const handleSelectPracticeGroups = (items: TPracticeGroup[]) => {
    setSelectedRegions(items.map((item) => item.id));

    // Get all practices from selected regions
    const practicesFromSelectedRegions = items.flatMap((group) =>
      group.practices.map((p) => p.practiceVideaId),
    );

    // Keep practices that are either:
    // 1. In the selected regions, OR
    // 2. Were manually selected and are not in any selected region
    const uniquePracticeIds =
      practices
        ?.filter((practice) => {
          const isInSelectedRegion = practicesFromSelectedRegions.includes(
            practice.id,
          );
          const isManuallySelected = selectedPractices?.includes(practice.id);
          const isInAnySelectedRegion = activePracticeGroups
            ?.filter((group) => selectedRegions.includes(group.id))
            .some((group) =>
              group.practices
                .map((p) => p.practiceVideaId)
                .includes(practice.id),
            );

          return (
            isInSelectedRegion || (isManuallySelected && !isInAnySelectedRegion)
          );
        })
        .map((p) => p.id) ?? [];

    setSelectedPractices(uniquePracticeIds);
    trackEvent(mixpanelEvents.insightsViewFilterChange, {
      filterType: 'region',
      selectedRegions: items.map((item) => item.id),
      selectedPractices: uniquePracticeIds,
    });
  };

  const handleSelectPractices = (items: string[]) => {
    setSelectedPractices(items);
    const practiceGroupsFromSelectedPractices = activePracticeGroups?.filter(
      (group) => {
        const groupPracticeIds = group.practices.map((p) => p.practiceVideaId);
        return groupPracticeIds.every((id) => items.includes(id));
      },
    );
    const newSelectedRegions =
      practiceGroupsFromSelectedPractices?.map((group) => group.id) ?? [];
    setSelectedRegions(newSelectedRegions);
    trackEvent(mixpanelEvents.insightsViewFilterChange, {
      filterType: 'practice',
      selectedPractices: items,
      selectedRegions: newSelectedRegions,
    });
  };

  const providerRoleItems: TProviderRole[] = ['HYGIENIST', 'DENTIST'];

  const filterButtonClass =
    'vai-h-9 vai-rounded-md vai-transition-colors vai-w-full';

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isPriorPeriodPickerOpen, setIsPriorPeriodPickerOpen] = useState(false);
  /**
   * When user selects "Custom" from the dropdown, we open the prior-period popover after a short
   * delay so the dropdown can close first. The dropdown's close can fire the popover's
   * onOpenChange(false). We ignore those close events for a brief window so the popover stays open.
   */
  const priorPickerOpenedByCustomRef = useRef<number | null>(null);
  const PRIOR_PICKER_OPEN_DELAY_MS = 100;
  const PRIOR_PICKER_IGNORE_CLOSE_MS = 400;

  const { options: dateRangePresets, referenceDate } =
    useDateRangePresetOptions();
  const formatDateRangeDisplay = useFormatDateRangeDisplay();

  const matchingDatePreset = useMemo(
    () => getMatchingDatePreset(startDate, endDate, referenceDate),
    [startDate, endDate, referenceDate],
  );

  const comparisonPresets = useMemo(
    () => getComparisonPresetRanges(startDate, endDate),
    [startDate, endDate],
  );

  const matchingComparisonPreset = useMemo(() => {
    if (priorStartDate == null || priorEndDate == null) return null;
    const priorStartDay = startOfDay(priorStartDate);
    const priorEndDay = startOfDay(priorEndDate);
    return (
      comparisonPresets.find(
        (p) =>
          startOfDay(p.start).getTime() === priorStartDay.getTime() &&
          startOfDay(p.end).getTime() === priorEndDay.getTime(),
      ) ?? null
    );
  }, [comparisonPresets, priorStartDate, priorEndDate]);

  const datePresetLabels = useDatePresetLabels();

  const comparisonPresetLabels: Record<TComparisonPresetKey, string> = {
    sameLength: t`Same length`,
    previousMonth: t`Previous month`,
    sameMonthLastYear: t`Same month last year`,
  };

  return (
    <div className="vai-rounded-lg vai-bg-muted/10 vai-py-3 vai-w-full">
      <div className="vai-flex vai-flex-col vai-gap-3 vai-w-full">
        {/* Row 1: pill date picker (left), Save / Custom views (right) */}
        <div
          className="vai-flex vai-flex-row vai-items-center vai-justify-between vai-gap-3 vai-flex-nowrap"
          role="group"
          aria-label={t`Date range and comparison period`}
        >
          <div className="vai-flex vai-flex-row vai-items-center vai-rounded-lg vai-border vai-border-border vai-bg-background vai-w-fit vai-overflow-hidden">
            {/* Left: current range — click opens calendar + presets */}
            <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="vai-flex vai-items-center vai-gap-2 vai-px-3 vai-py-2 vai-text-left vai-text-sm vai-font-medium hover:vai-bg-muted/50 vai-transition-colors vai-min-w-[200px]"
                  aria-label={t`Select date range`}
                >
                  {formatDateRangeDisplay(startDate, endDate)}
                </button>
              </PopoverTrigger>
              <PopoverContent className="vai-w-auto vai-p-0" align="start">
                <div className="vai-flex vai-flex-col">
                  <div className="vai-flex vai-flex-col vai-gap-1.5 vai-p-2 vai-border-b vai-border-border">
                    <span className="vai-text-xs vai-font-medium vai-text-muted-foreground">
                      <Trans>Presets</Trans>
                    </span>
                    <div className="vai-flex vai-flex-wrap vai-gap-1">
                      {dateRangePresets.map((preset) => {
                        const isSelected =
                          matchingDatePreset?.value === preset.value;
                        return (
                          <Button
                            key={preset.value}
                            variant={isSelected ? 'default' : 'ghost'}
                            size="sm"
                            className={cn(
                              'vai-h-8 vai-text-xs',
                              isSelected &&
                                'vai-bg-primary vai-text-primary-foreground hover:vai-bg-primary/90 hover:vai-text-primary-foreground',
                            )}
                            onClick={() => {
                              const { start, end } = preset.getRange();
                              setStartDate(start);
                              setEndDate(end);
                              trackEvent(
                                mixpanelEvents.insightsViewFilterChange,
                                {
                                  filterType: 'date',
                                  startDate: start,
                                  endDate: end,
                                  dateRangePreset: preset.value,
                                },
                              );
                            }}
                          >
                            {datePresetLabels[preset.value]}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={startDate}
                    selected={{ from: startDate, to: endDate }}
                    onSelect={(range) => {
                      const start = range?.from ?? startDate;
                      const end = range?.to ?? endDate;
                      setStartDate(start);
                      setEndDate(end);
                      trackEvent(mixpanelEvents.insightsViewFilterChange, {
                        filterType: 'date',
                        startDate: start,
                        endDate: end,
                      });
                    }}
                    numberOfMonths={2}
                  />
                </div>
              </PopoverContent>
            </Popover>
            {/* Comparison mode — "versus" opens dropdown (Trailing / YoY / Custom) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="vai-flex vai-items-center vai-gap-1 vai-px-2 vai-py-1.5 vai-text-[10pt] vai-text-muted-foreground hover:vai-bg-muted/50 hover:vai-text-foreground vai-rounded-md vai-transition-colors vai-cursor-pointer vai-border-0 vai-bg-transparent"
                  aria-label={t`Comparison period options`}
                >
                  <Trans>versus</Trans>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="vai-h-2.5 vai-w-2.5 vai-opacity-70"
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="vai-min-w-[280px]">
                <DropdownMenuRadioGroup
                  value={comparisonPeriodMode}
                  onValueChange={(value) => {
                    const mode = value as 'trailing' | 'yoy' | 'custom';
                    setComparisonPeriodMode(mode);
                    setPriorStartDate(null);
                    setPriorEndDate(null);
                    if (mode === 'custom') {
                      const openedAt = Date.now();
                      priorPickerOpenedByCustomRef.current = openedAt;
                      setTimeout(
                        () => setIsPriorPeriodPickerOpen(true),
                        PRIOR_PICKER_OPEN_DELAY_MS,
                      );
                      setTimeout(() => {
                        if (priorPickerOpenedByCustomRef.current === openedAt) {
                          priorPickerOpenedByCustomRef.current = null;
                        }
                      }, PRIOR_PICKER_IGNORE_CLOSE_MS);
                    }
                    trackEvent(mixpanelEvents.insightsViewFilterChange, {
                      filterType: 'comparisonPeriod',
                      comparisonPeriodMode: mode,
                    });
                  }}
                >
                  <DropdownMenuRadioItem value="trailing" className="vai-flex vai-items-center vai-justify-between vai-gap-2">
                    <Trans>Trailing days</Trans>
                    <span className="vai-text-right vai-text-muted-foreground vai-text-xs vai-shrink-0">
                      {t`Same length, ends day before`}
                    </span>
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="yoy" className="vai-flex vai-items-center vai-justify-between vai-gap-2">
                    <Trans>Year-over-year</Trans>
                    <span className="vai-text-right vai-text-muted-foreground vai-text-xs vai-shrink-0">
                      {t`Same period last year`}
                    </span>
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="custom" className="vai-flex vai-items-center vai-justify-between vai-gap-2">
                    <Trans>Custom</Trans>
                    <span className="vai-text-right vai-text-muted-foreground vai-text-xs vai-shrink-0">
                      {t`Pick any range`}
                    </span>
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Prior period dates (lighter gray); when Custom, click opens calendar + presets */}
            {comparisonPeriodMode === 'custom' ? (
              <Popover
                open={isPriorPeriodPickerOpen}
                onOpenChange={(open) => {
                  if (!open) {
                    const openedAt = priorPickerOpenedByCustomRef.current;
                    if (
                      typeof openedAt === 'number' &&
                      Date.now() - openedAt < PRIOR_PICKER_IGNORE_CLOSE_MS
                    ) {
                      return;
                    }
                    priorPickerOpenedByCustomRef.current = null;
                  }
                  setIsPriorPeriodPickerOpen(open);
                }}
              >
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="vai-flex vai-items-center vai-gap-2 vai-px-3 vai-py-2 vai-text-left vai-text-sm vai-font-medium vai-text-foreground hover:vai-bg-muted/50 vai-transition-colors vai-rounded-md"
                    aria-label={t`Select comparison date range`}
                  >
                    {priorStartDate && priorEndDate
                      ? formatDateRangeDisplay(priorStartDate, priorEndDate)
                      : t`Select dates`}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="vai-w-auto vai-p-0" align="start">
                  <div className="vai-flex vai-flex-col">
                    <div className="vai-flex vai-flex-col vai-gap-1.5 vai-p-2 vai-border-b vai-border-border">
                      <span className="vai-text-xs vai-font-medium vai-text-muted-foreground">
                        <Trans>Presets</Trans>
                      </span>
                      <div className="vai-flex vai-flex-wrap vai-gap-1">
                        {comparisonPresets.map((preset) => {
                          const isSelected =
                            matchingComparisonPreset?.key === preset.key;
                          return (
                            <Button
                              key={preset.key}
                              variant={isSelected ? 'default' : 'ghost'}
                              size="sm"
                              className={cn(
                                'vai-h-8 vai-text-xs',
                                isSelected &&
                                  'vai-bg-primary vai-text-primary-foreground hover:vai-bg-primary/90 hover:vai-text-primary-foreground',
                              )}
                              onClick={() => {
                                setPriorStartDate(preset.start);
                                setPriorEndDate(preset.end);
                                trackEvent(
                                  mixpanelEvents.insightsViewFilterChange,
                                  {
                                    filterType: 'priorPeriod',
                                    priorStartDate: preset.start,
                                    priorEndDate: preset.end,
                                    priorPeriodPreset: preset.key,
                                  },
                                );
                              }}
                            >
                              {comparisonPresetLabels[preset.key]}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                    <Calendar
                      initialFocus
                      mode="range"
                      defaultMonth={priorStartDate ?? subDays(startDate, 1)}
                      selected={
                        priorStartDate
                          ? {
                              from: priorStartDate,
                              to: priorEndDate ?? undefined,
                            }
                          : undefined
                      }
                      onSelect={(range) => {
                        let from = range?.from ?? null;
                        // When range?.to is undefined (first click of a new range), use from so we don't
                        // fall back to priorEndDate—otherwise clicking a date after the old end would
                        // give from > to and clamping would collapse both to the old end, trapping the user.
                        let to =
                          range?.to ??
                          (from != null ? from : priorEndDate) ??
                          null;
                        const clamped = clampPriorPeriodToBeforeCurrent(
                          from,
                          to,
                          startDate,
                        );
                        from = clamped.from;
                        to = clamped.to;
                        setPriorStartDate(from);
                        setPriorEndDate(to);
                        if (from != null || to != null) {
                          trackEvent(mixpanelEvents.insightsViewFilterChange, {
                            filterType: 'priorPeriod',
                            priorStartDate: from,
                            priorEndDate: to,
                          });
                        }
                      }}
                      numberOfMonths={2}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <span
                className="vai-px-3 vai-py-2 vai-text-sm vai-text-muted-foreground vai-whitespace-nowrap"
                title={formatDateRangeDisplay(priorStart, priorEnd)}
              >
                {formatDateRangeDisplay(priorStart, priorEnd)}
              </span>
            )}
          </div>
          <div className="vai-h-9 vai-flex-shrink-0 vai-border-l vai-border-border/50 vai-pl-3 vai-flex vai-items-center">
            <SavedViews />
          </div>
        </div>

        {/* Row 2: filter dropdowns (left), MaterialityFilter + $/# (right) */}
        <div className="vai-flex vai-flex-row vai-justify-between vai-gap-3 vai-flex-wrap vai-items-center vai-border-t vai-border-border/50 vai-pt-3">
          <div className="vai-flex vai-flex-row vai-gap-2 vai-flex-wrap vai-items-center">
            {enablePracticeFilter && (
              <>
                <div className="vai-w-auto">
                  <SearchableCheckboxDropdown<TPracticeGroup>
                    items={activePracticeGroups || []}
                    selectedItems={
                      activePracticeGroups?.filter((group) =>
                        selectedRegions.includes(group.id),
                      ) || []
                    }
                    onChange={(items) => handleSelectPracticeGroups(items)}
                    getItemLabel={(item: TPracticeGroup) => item.name}
                    getItemValue={(item: TPracticeGroup) => item.id}
                    placeholder={t`Regions`}
                    selectionPhrase={t`Region`}
                    buttonClassName={filterButtonClass}
                    includeAllOption={true}
                    isUnsorted={true}
                  />
                </div>
                <div className="vai-w-auto">
                  <SearchableCheckboxDropdown<{ id: string; name: string }>
                    getItemLabel={(item) => item.name}
                    getItemValue={(item) => item.id}
                    items={practices}
                    selectedItems={practices.filter((p) =>
                      selectedPractices.includes(p.id),
                    )}
                    selectionPhrase={t`Practice`}
                    placeholder={t`Practices`}
                    onChange={(items) =>
                      handleSelectPractices(items.map((p) => p.id))
                    }
                    buttonClassName={filterButtonClass}
                    includeAllOption={true}
                    isUnsorted={true}
                  />
                </div>
              </>
            )}

            {enableProviderRoleFilter && (
              <div className="vai-w-auto">
                <CheckboxDropdown
                  getItemLabel={(item) =>
                    `${item?.[0]?.toUpperCase()}${item?.slice(1).toLowerCase()}`
                  }
                  getItemValue={(item) => item}
                  items={providerRoleItems}
                  selectedItems={selectedProviderRole}
                  placeholder={t`Provider Role`}
                  onChange={(items) => {
                    setSelectedProviderRole(items);
                    trackEvent(mixpanelEvents.insightsViewFilterChange, {
                      filterType: 'providerRole',
                      providerRole: items,
                    });
                  }}
                  includeAllOption={true}
                  buttonClassName={filterButtonClass}
                />
              </div>
            )}

            {!enableTasksFilter && (
              <div className="vai-w-auto">
                <CheckboxDropdown
                  getItemLabel={(item) => item}
                  getItemValue={(item) => item}
                  items={availableTreatments}
                  selectedItems={txType}
                  placeholder={t`Treatments`}
                  onChange={(items) => {
                    setTxType(items);
                    trackEvent(mixpanelEvents.insightsViewFilterChange, {
                      filterType: 'txType',
                      txType: items,
                    });
                  }}
                  includeAllOption={true}
                  buttonClassName={filterButtonClass}
                />
              </div>
            )}

            {enableTasksFilter && (
              <div className="vai-w-auto">
                <CheckboxDropdown
                  getItemLabel={(item) => item}
                  getItemValue={(item) => item}
                  items={availableTasks}
                  selectedItems={taskCodesToTypes(taskType)}
                  placeholder={t`Tasks`}
                  onChange={(items) => {
                    setTaskType(taskTypesToCodes(items));
                    trackEvent(mixpanelEvents.insightsViewFilterChange, {
                      filterType: 'taskType',
                      taskType: items,
                    });
                  }}
                  includeAllOption={true}
                  buttonClassName={filterButtonClass}
                />
              </div>
            )}
          </div>
          <div className="vai-flex vai-flex-row vai-gap-1 vai-items-center vai-flex-shrink-0 vai-border-l vai-border-border/50 vai-pl-3">
            {enableMaterialityFilter && (
              <MaterialityFilter
                productionValue={
                  isCounts
                    ? materialityFilterProductionCounts
                    : materialityFilterProductionDollars
                }
                setProductionValue={
                  isCounts
                    ? setMaterialityFilterProductionCounts
                    : setMaterialityFilterProductionDollars
                }
                opportunityValue={
                  isCounts
                    ? materialityFilterOpportunityCounts
                    : materialityFilterOpportunityDollars
                }
                setOpportunityValue={
                  isCounts
                    ? setMaterialityFilterOpportunityCounts
                    : setMaterialityFilterOpportunityDollars
                }
                max={isCounts ? 10 : 5000}
                isCounts={isCounts}
              />
            )}
            <Button
              className={cn(
                'vai-w-10 vai-h-9 vai-rounded-md vai-transition-colors',
                !isCounts
                  ? 'vai-bg-sapphire-50 vai-text-primary hover:vai-bg-sapphire-100 hover:vai-text-primary'
                  : 'vai-bg-white hover:vai-bg-zinc-100',
              )}
              onClick={() => setIsCounts(false)}
              variant="outline"
            >
              <FontAwesomeIcon
                className={cn(!isCounts && 'vai-text-primary')}
                icon={faDollarSign}
              />
            </Button>
            <Button
              className={cn(
                'vai-w-10 vai-h-9 vai-rounded-md vai-transition-colors',
                isCounts
                  ? 'vai-bg-sapphire-50 vai-text-primary hover:vai-bg-sapphire-100 hover:vai-text-primary'
                  : 'vai-bg-white hover:vai-bg-zinc-100',
              )}
              onClick={() => setIsCounts(true)}
              variant="outline"
            >
              <FontAwesomeIcon
                className={cn(isCounts && 'vai-text-primary')}
                icon={faHashtag}
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
